import React, { useState } from 'react';
import { X } from 'lucide-react';
import { puntovService } from '../../../services/api';
import CampoTelefono from './CampoTelefono';
import CampoContrasena, { RequisitosContrasena, contrasenaValida } from './CampoContrasena';
import SelectorStockPrueba from './SelectorStockPrueba';
import { paisPorIso, PAIS_PRINCIPAL } from './paises';

// Sugiere el identificador de la base a partir del nombre: sin acentos,
// minúsculas y guion bajo. Punto V crea la base como esgamanc_<slug>.
const sugerirSlug = (nombre) => nombre
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 32);

const VACIO = { nombre: '', slug: '', correo: '', password: '' };
const TELEFONO_VACIO = { iso: PAIS_PRINCIPAL, numero: '' };

// Punto V guarda el teléfono como dígitos con código de país y sin el 0
// troncal (584141234567): así lo usa como número de origen en pagos C2P.
const numeroInternacional = ({ iso, numero }) =>
  paisPorIso(iso).codigo + numero.replace(/\D/g, '').replace(/^0+/, '');

const Campo = ({ label, children, ayuda }) => (
  <div>
    <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
    {children}
    {ayuda && <p className="text-xs text-gray-400 mt-1">{ayuda}</p>}
  </div>
);

const claseInput = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500';

const FormularioCliente = ({ onCreado, onCancel }) => {
  const [form, setForm] = useState(VACIO);
  const [slugEditado, setSlugEditado] = useState(false);
  const [telefono, setTelefono] = useState(TELEFONO_VACIO);
  const [confirmacion, setConfirmacion] = useState('');
  const [almacenPrueba, setAlmacenPrueba] = useState(false);
  const [almacenConStock, setAlmacenConStock] = useState(true);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const cambiar = (campo, valor) => setForm(prev => {
    const siguiente = { ...prev, [campo]: valor };
    if (campo === 'nombre' && !slugEditado) siguiente.slug = sugerirSlug(valor);
    return siguiente;
  });

  const enviar = async (e) => {
    e.preventDefault();
    setError('');
    if (!contrasenaValida(form.password)) {
      setError('La contraseña no cumple los requisitos indicados debajo del campo.');
      return;
    }
    if (form.password !== confirmacion) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setLoading(true);
    try {
      const res = await puntovService.crearCliente({
        ...form,
        numero: numeroInternacional(telefono),
        almacen_prueba: almacenPrueba,
        almacen_con_stock: almacenConStock
      });
      setForm(VACIO);
      setTelefono(TELEFONO_VACIO);
      setConfirmacion('');
      setAlmacenPrueba(false);
      setAlmacenConStock(true);
      onCreado(res.data);
    } catch (err) {
      setError(err.message || 'Error al crear el cliente');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={enviar} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-gray-800">Nuevo cliente Punto V</h3>
        <button type="button" onClick={onCancel} className="text-gray-400 hover:text-gray-600">
          <X size={18} />
        </button>
      </div>
      <p className="text-sm text-gray-500">
        Crea la base de datos del cliente en Punto V y su usuario dueño con rol Administrador.
        Con ese correo y contraseña el cliente entra a Punto V Manager.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Campo label="Nombre del dueño *" ayuda="Máximo 50 caracteres">
          <input
            value={form.nombre}
            onChange={(e) => cambiar('nombre', e.target.value)}
            required
            maxLength={50}
            className={claseInput}
          />
        </Campo>
        <Campo label="Nombre de la base de datos *" ayuda="Punto V agrega el prefijo esgamanc_ siempre. Letras, números y guion bajo.">
          <div className="flex rounded-lg border border-gray-300 focus-within:ring-2 focus-within:ring-blue-500 overflow-hidden">
            <span className="px-3 py-2 bg-gray-50 text-gray-500 text-sm font-mono border-r border-gray-300 select-none">
              esgamanc_
            </span>
            <input
              value={form.slug}
              onChange={(e) => {
                setSlugEditado(true);
                cambiar('slug', e.target.value.toLowerCase().replace(/^esgamanc_/, ''));
              }}
              required
              maxLength={32}
              pattern="[a-z0-9_]+"
              title="Solo letras minúsculas, números y guion bajo"
              className="flex-1 min-w-0 px-3 py-2 text-sm font-mono outline-none"
            />
          </div>
        </Campo>
        <Campo label="Correo *">
          <input
            type="email"
            value={form.correo}
            onChange={(e) => cambiar('correo', e.target.value)}
            required
            className={claseInput}
          />
        </Campo>
        <Campo label="Teléfono *">
          <CampoTelefono value={telefono} onChange={setTelefono} required />
        </Campo>
        <div>
          <label htmlFor="cliente-password" className="block text-xs font-medium text-gray-700 mb-1">Contraseña inicial *</label>
          <CampoContrasena
            id="cliente-password"
            value={form.password}
            onChange={(v) => cambiar('password', v)}
            invalido={form.password !== '' && !contrasenaValida(form.password)}
            required
          />
          <RequisitosContrasena value={form.password} />
        </div>
        <div>
          <label htmlFor="cliente-password-confirmacion" className="block text-xs font-medium text-gray-700 mb-1">Confirmar contraseña *</label>
          <CampoContrasena
            id="cliente-password-confirmacion"
            value={confirmacion}
            onChange={setConfirmacion}
            invalido={confirmacion !== '' && confirmacion !== form.password}
            required
          />
          {confirmacion !== '' && (
            confirmacion === form.password
              ? <p className="text-xs text-green-700 mt-2">Las contraseñas coinciden</p>
              : <p className="text-xs text-red-600 mt-2">Las contraseñas no coinciden</p>
          )}
        </div>
      </div>

      <label htmlFor="cliente-almacen-prueba" className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
        <input
          id="cliente-almacen-prueba"
          type="checkbox"
          checked={almacenPrueba}
          onChange={(e) => setAlmacenPrueba(e.target.checked)}
          className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span>
          Cargar almacén de prueba
          <span className="block text-xs text-gray-400">
            Llena el almacén del cliente con el catálogo de prueba de Punto V (productos, costos, precios e imágenes).
          </span>
        </span>
      </label>
      {almacenPrueba && (
        <div className="pl-6 max-w-md">
          <SelectorStockPrueba id="cliente-almacen-stock" conStock={almacenConStock} onChange={setAlmacenConStock} />
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>
      )}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
        >
          {loading ? 'Creando…' : 'Crear cliente'}
        </button>
      </div>
    </form>
  );
};

export default FormularioCliente;
